import { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Check, X, UserCheck, Clock, Building2, TrendingUp, Users, Briefcase, Trash2, Loader2, FileSignature, Receipt, Coins, Save, CreditCard, Upload, FileUp, FileText, Mail, PenLine, UserX, KeyRound, Search, History, GitMerge, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

function initials(n = "") {
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function auditLabel(action) {
  return {
    edit_user: "redigerte",
    grant_admin: "ga admin-tilgang til",
    revoke_admin: "fjernet admin-tilgang fra",
    reset_link: "sendte passord-lenke til",
    delete_user: "slettet",
  }[action] || "endret";
}

function auditIcon(action) {
  const cls = "h-3.5 w-3.5 text-muted-foreground";
  if (action === "grant_admin" || action === "revoke_admin") return <ShieldCheck className={cls} />;
  if (action === "reset_link") return <KeyRound className={cls} />;
  if (action === "delete_user") return <Trash2 className={cls} />;
  return <PenLine className={cls} />;
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [creqs, setCreqs] = useState(null);
  const [vreqs, setVreqs] = useState(null);
  const [comps, setComps] = useState(null);
  const [delCompany, setDelCompany] = useState(null);
  const [mergeGroup, setMergeGroup] = useState(null); // array of duplicate companies
  const [mergeTarget, setMergeTarget] = useState(null); // id to keep
  const [merging, setMerging] = useState(false);

  // Mulige duplikater: grupper på normalisert selskapsnavn (fanger også duplikat der ett
  // mangler org.nr). id -> gruppe, kun for grupper med mer enn ett selskap.
  const dupMap = useMemo(() => {
    const list = comps || [];
    const groups = {};
    list.forEach((c) => {
      const key = (c.name || "").trim().toLowerCase();
      (groups[key] ||= []).push(c);
    });
    const byId = {};
    list.forEach((c) => {
      const grp = groups[(c.name || "").trim().toLowerCase()];
      if (grp && grp.length > 1) byId[c.id] = grp;
    });
    return byId;
  }, [comps]);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [lics, setLics] = useState(null);
  const [lsettings, setLsettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [approveUpload, setApproveUpload] = useState(null);
  const [af, setAf] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejectUpload, setRejectUpload] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingSigs, setPendingSigs] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [users, setUsers] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "" });
  const [savingUser, setSavingUser] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [audit, setAudit] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = () => api.get("/admin/verifications").then((r) => setRows(r.data)).catch(() => setRows([]));
  const loadCreqs = () => api.get("/admin/company-requests").then((r) => setCreqs(r.data)).catch(() => setCreqs([]));
  const loadVreqs = () => api.get("/admin/valuation-requests").then((r) => setVreqs(r.data)).catch(() => setVreqs([]));
  const loadComps = () => api.get("/admin/companies").then((r) => setComps(r.data)).catch(() => setComps([]));
  const loadLics = () => api.get("/admin/licenses").then((r) => setLics(r.data)).catch(() => setLics({ licenses: [], summary: {} }));
  const loadLsettings = () => api.get("/admin/license-settings").then((r) => setLsettings(r.data)).catch(() => setLsettings(null));
  const loadUploads = () => api.get("/admin/share-uploads").then((r) => setUploads(r.data)).catch(() => setUploads([]));
  const loadPendingSigs = () => api.get("/admin/pending-signatures").then((r) => setPendingSigs(r.data)).catch(() => setPendingSigs([]));
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  const loadAudit = () => api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => setAudit([]));

  useEffect(() => {
    load();
    loadCreqs();
    loadVreqs();
    loadComps();
    loadLics();
    loadLsettings();
    loadUploads();
    loadPendingSigs();
    loadUsers();
    loadAudit();
  }, []);

  if (user && !user.is_admin) return <Navigate to="/hjem" replace />;

  const confirmDeleteUser = async () => {
    if (!delUser) return;
    setDeletingUser(true);
    try {
      const { data } = await api.delete(`/admin/users/${delUser.id}`);
      toast.success(`Slettet brukeren ${data.name}`);
      setDelUser(null);
      loadUsers();
      loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeletingUser(false);
    }
  };

  const sendReset = async (u) => {
    if (!u.email) { toast.error("Brukeren har ingen e-postadresse"); return; }
    setResettingId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      if (data.emailed) toast.success(`Passord-lenke sendt til ${data.email}`);
      else toast.warning(`Lenke opprettet, men e-posten kunne ikke sendes til ${data.email}`);
      loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResettingId(null);
    }
  };

  const toggleAdmin = async (u, next) => {
    setTogglingId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/role`, { is_admin: next });
      toast.success(next ? `${u.name} er nå administrator` : `Admin-tilgang fjernet for ${u.name}`);
      loadUsers();
      loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditUserForm({ name: u.name || "", email: u.email || "" });
  };

  const saveUser = async () => {
    if (!editUser) return;
    if (!editUserForm.name.trim()) { toast.error("Navn kan ikke være tomt"); return; }
    if (!editUserForm.email.trim() || !editUserForm.email.includes("@")) { toast.error("Ugyldig e-postadresse"); return; }
    setSavingUser(true);
    try {
      await api.put(`/admin/users/${editUser.id}`, { name: editUserForm.name, email: editUserForm.email });
      toast.success("Brukeren er oppdatert");
      setEditUser(null);
      loadUsers();
      loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingUser(false);
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/verifications/${id}/${action}`);
      toast.success(action === "approve" ? "Bruker verifisert" : "Forespørsel avvist");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const actCompany = async (id, action) => {
    try {
      await api.post(`/admin/company-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Selskap opprettet / tildelt" : "Forespørsel avvist");
      loadCreqs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const actValuation = async (id, action) => {
    try {
      await api.post(`/admin/valuation-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Verdsettelse godkjent" : "Verdsettelse avvist");
      loadVreqs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openDelete = (c) => {
    setDelCompany(c);
    setConfirmText("");
  };

  const openMerge = (group) => {
    // standard: behold den med org.nr, ellers flest medlemmer
    const target = [...group].sort((a, b) => {
      const ao = (a.org_number || "").replace(/\D/g, "") ? 1 : 0;
      const bo = (b.org_number || "").replace(/\D/g, "") ? 1 : 0;
      if (ao !== bo) return bo - ao;
      return (b.member_count || 0) - (a.member_count || 0);
    })[0];
    setMergeGroup(group);
    setMergeTarget(target?.id || null);
  };

  const doMerge = async () => {
    if (!mergeGroup || !mergeTarget) return;
    const sources = mergeGroup.filter((c) => c.id !== mergeTarget);
    if (sources.length === 0) { setMergeGroup(null); return; }
    setMerging(true);
    try {
      for (const s of sources) {
        await api.post("/admin/companies/merge", { source_id: s.id, target_id: mergeTarget });
      }
      const keep = mergeGroup.find((c) => c.id === mergeTarget);
      toast.success(`${sources.length} duplikat slått sammen til «${keep?.name}»`);
      setMergeGroup(null);
      setMergeTarget(null);
      loadComps();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMerging(false);
    }
  };

  const confirmDelete = async () => {
    if (!delCompany) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/companies/${delCompany.id}`);
      toast.success(`${data.name || delCompany.name} slettet permanent`);
      setDelCompany(null);
      loadComps();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const togglePayment = async (l) => {
    setPayingId(l.id);
    const next = l.payment_status === "paid" ? "unpaid" : "paid";
    try {
      await api.post(`/admin/licenses/${l.id}/payment`, { payment_status: next });
      toast.success(next === "paid" ? "Markert som betalt" : "Markert som ikke betalt");
      loadLics();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPayingId(null);
    }
  };

  const setField = (k, v) => setLsettings((s) => ({ ...s, [k]: v }));

  const openApprove = (u) => {
    setApproveUpload(u);
    const isEmission = u.doc_type === "emisjon";
    setAf({
      date: new Date().toISOString().slice(0, 10),
      type: isEmission ? "Emisjon" : u.doc_type === "avtale" ? "Aksjekjøpsavtale (opplastet)" : "Aksjeoverdragelse (opplastet)",
      share_class: u.share_class || "",
      shares: u.shares || "",
      price_per_share: u.price_per_share || "",
      from_name: isEmission ? "" : u.seller_name || "",
      to_name: u.buyer_name || "",
      from_org: isEmission ? "" : u.seller_org || "",
      to_org: u.buyer_org || "",
      note: u.note || "",
    });
  };
  const setAff = (k, v) => setAf((s) => ({ ...s, [k]: v }));

  const confirmApprove = async () => {
    const hasSubs = (approveUpload?.subscribers || []).length > 0;
    if (!af.share_class || (!hasSubs && (!af.to_name || !af.shares))) {
      toast.error(hasSubs ? "Fyll inn aksjeklasse" : "Fyll inn aksjeklasse, antall og kjøper");
      return;
    }
    setApproving(true);
    try {
      await api.post(`/admin/share-uploads/${approveUpload.id}/approve`, {
        date: af.date, type: af.type, share_class: af.share_class,
        shares: parseInt(af.shares, 10), price_per_share: parseFloat(af.price_per_share || 0),
        from_name: af.from_name, to_name: af.to_name, note: af.note,
        from_org: af.from_org, to_org: af.to_org,
      });
      toast.success("Transaksjon registrert i aksjeboka");
      setApproveUpload(null);
      loadUploads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const confirmReject = async () => {
    try {
      await api.post(`/admin/share-uploads/${rejectUpload.id}/reject`, { reason: rejectReason });
      toast.success("Opplasting avvist");
      setRejectUpload(null);
      setRejectReason("");
      loadUploads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPendingPdf = (s) =>
    openSigned(`/admin/pending-signatures/${s.entity_type}/${s.entity_id}/pdf-link`).catch(() => toast.error("Kunne ikke åpne dokumentet"));

  const remindParty = async (p) => {
    if (!p.signing_request_id) return;
    setRemindingId(p.signing_request_id);
    try {
      const { data } = await api.post(`/admin/signing-requests/${p.signing_request_id}/remind`);
      const to = Array.isArray(data.sent_to) ? data.sent_to.join(", ") : data.sent_to;
      toast.success(`Purring sendt${to ? ` til ${to}` : ""}`);
      loadPendingSigs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRemindingId(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/license-settings", lsettings);
      setLsettings(data);
      toast.success("Lisensinnstillinger lagret");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const nok = (v) => (v || v === 0 ? `${Number(v).toLocaleString("nb-NO")} kr` : "–");
  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" }) : "–"; } catch { return "–"; } };
  const fmtDateTime = (d) => { try { return d ? new Date(d).toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "–"; } catch { return "–"; } };
  const filteredUsers = (users || []).filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b border-border/60 bg-background/80 px-5 backdrop-blur-xl sm:px-8">
        <Logo subtitle="Admin" />
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/hjem")}>
            <ArrowLeft className="h-4 w-4" /> Dashbord
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <div className="flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Verifiseringsforespørsler</h1>
            <p className="text-sm text-muted-foreground">Godkjenn eller avvis brukere som har bedt om admin-verifisering.</p>
          </div>
        </div>

        <div className="mt-8 space-y-3" data-testid="admin-verifications-list">
          {rows === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen ventende forespørsler akkurat nå.
            </div>
          ) : (
            rows.map((u, i) => (
              <motion.div
                key={u.id}
                data-testid={`admin-request-${u.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-headings text-base font-bold tracking-tight">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email} · Fødselsnr {u.national_id || "–"}</p>
                  {u.verification_note ? <p className="mt-1 text-xs italic text-muted-foreground">«{u.verification_note}»</p> : null}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`reject-${u.id}`} onClick={() => act(u.id, "reject")}>
                    <X className="h-4 w-4" /> Avvis
                  </Button>
                  <Button size="sm" className="gap-1.5" data-testid={`approve-${u.id}`} onClick={() => act(u.id, "approve")}>
                    <Check className="h-4 w-4" /> Godkjenn
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Selskapsforespørsler</h1>
            <p className="text-sm text-muted-foreground">Brukere som ber om tilgang til et selskap som ikke fantes i Proff-lista.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-company-requests-list">
          {creqs === null ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : creqs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen ventende selskapsforespørsler.
            </div>
          ) : (
            creqs.map((r, i) => (
              <motion.div
                key={r.id}
                data-testid={`admin-company-request-${r.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">{initials(r.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 font-headings text-base font-bold tracking-tight">
                      {r.name}
                      {r.kind === "manual" ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Manuell</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {r.org_number ? `Org.nr ${r.org_number} · ` : ""}Rolle: {r.role} · Bedt av {r.user_name} ({r.user_email})
                    </p>
                    {r.note ? <p className="mt-1 text-xs italic text-muted-foreground">«{r.note}»</p> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`company-reject-${r.id}`} onClick={() => actCompany(r.id, "reject")}>
                      <X className="h-4 w-4" /> Avvis
                    </Button>
                    <Button size="sm" className="gap-1.5" data-testid={`company-approve-${r.id}`} onClick={() => actCompany(r.id, "approve")}>
                      <Check className="h-4 w-4" /> {r.kind === "manual" ? "Opprett selskap" : "Gi tilgang"}
                    </Button>
                  </div>
                </div>

                {r.kind === "manual" && r.details ? (
                  <div className="mt-4 grid gap-4 rounded-lg border border-border/60 bg-muted/20 p-4 text-xs sm:grid-cols-3" data-testid={`manual-details-${r.id}`}>
                    <div>
                      <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground"><Building2 className="h-3.5 w-3.5 text-primary" /> Selskap</p>
                      <p className="text-muted-foreground">{[r.details.address, `${r.details.postal_code || ""} ${r.details.city || ""}`.trim()].filter(Boolean).join(", ") || "Ingen adresse"}</p>
                      <p className="mt-1 text-muted-foreground">Aksjekapital: {nok(r.details.share_capital)}</p>
                      <p className="text-muted-foreground">Antall aksjer: {(r.details.total_shares || 0).toLocaleString("nb-NO")} ({r.details.share_class_name || "Ordinære aksjer"})</p>
                    </div>
                    <div>
                      <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground"><Users className="h-3.5 w-3.5 text-primary" /> Eiere ({(r.details.owners || []).length})</p>
                      {(r.details.owners || []).length ? (r.details.owners).map((o, idx) => (
                        <p key={idx} className="text-muted-foreground">{o.name} — {(o.shares || 0).toLocaleString("nb-NO")} aksjer{o.email ? ` · ${o.email}` : ""}</p>
                      )) : <p className="text-muted-foreground">Ingen oppgitt</p>}
                    </div>
                    <div>
                      <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground"><Briefcase className="h-3.5 w-3.5 text-primary" /> Styret ({(r.details.board || []).length})</p>
                      {(r.details.board || []).length ? (r.details.board).map((b, idx) => (
                        <p key={idx} className="text-muted-foreground">{b.name} — {b.role}{b.email ? ` · ${b.email}` : ""}</p>
                      )) : <p className="text-muted-foreground">Ingen oppgitt</p>}
                    </div>
                  </div>
                ) : null}
              </motion.div>
            ))
          )}
        </div>

        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><TrendingUp className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Verdsettelsesforespørsler</h1>
            <p className="text-sm text-muted-foreground">Styreledere/daglig ledere som foreslår en verdsettelse. Godkjenn eller avvis.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-valuation-requests-list">
          {vreqs === null ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : vreqs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen ventende verdsettelsesforespørsler.
            </div>
          ) : (
            vreqs.map((r, i) => (
              <motion.div
                key={r.id}
                data-testid={`admin-valuation-request-${r.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><TrendingUp className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-headings text-base font-bold tracking-tight">{r.company_name}</p>
                  <p className="text-xs text-muted-foreground">Foreslått verdsettelse: <span className="font-semibold text-foreground">{nok(r.valuation)}</span> · av {r.proposed_by_name}</p>
                  {r.note ? <p className="mt-1.5 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-foreground" data-testid={`valuation-note-${r.id}`}>{r.note}</p> : null}
                  {r.attachment ? (
                    <Button size="sm" variant="outline" className="mt-2 h-7 gap-1.5 text-xs" data-testid={`valuation-attachment-${r.id}`}
                      onClick={() => openSigned(`/valuation-requests/${r.id}/file-link`).catch(() => toast.error("Kunne ikke åpne dokumentet"))}>
                      <FileText className="h-3.5 w-3.5" /> {r.attachment.file_name || "Se dokumentasjon"}
                    </Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`valuation-reject-${r.id}`} onClick={() => actValuation(r.id, "reject")}>
                    <X className="h-4 w-4" /> Avvis
                  </Button>
                  <Button size="sm" className="gap-1.5" data-testid={`valuation-approve-${r.id}`} onClick={() => actValuation(r.id, "approve")}>
                    <Check className="h-4 w-4" /> Godkjenn
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><FileUp className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Opplastede avtaler til godkjenning</h1>
            <p className="text-sm text-muted-foreground">Sluttsedler/avtaler brukere har lastet opp. Godkjenn for å registrere transaksjonen i aksjeboka.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-share-uploads-list">
          {uploads === null ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : uploads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <FileUp className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen opplastede avtaler akkurat nå.
            </div>
          ) : (
            uploads.map((u, i) => {
              const st = u.status === "godkjent"
                ? { label: "Godkjent", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" }
                : u.status === "avvist"
                ? { label: "Avvist", cls: "bg-muted text-muted-foreground" }
                : { label: "Venter", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" };
              return (
                <motion.div
                  key={u.id}
                  data-testid={`admin-upload-${u.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="rounded-xl border border-border/60 bg-card p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" strokeWidth={1.7} /></div>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 font-headings text-base font-bold tracking-tight">
                        {u.company_name} · {u.doc_type === "emisjon" ? "Emisjon" : u.doc_type === "avtale" ? "Aksjekjøpsavtale" : "Sluttseddel"}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${st.cls}`}>{st.label}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {u.doc_type === "emisjon" ? "Nyutstedelse" : (u.seller_name || "?")} → {(u.buyer_name || "?")}
                        {u.shares ? ` · ${Number(u.shares).toLocaleString("nb-NO")} ${u.share_class}` : (u.share_class ? ` · ${u.share_class}` : "")}
                        {u.price_per_share ? ` · ${nok(u.price_per_share)}/aksje` : ""} · lastet opp av {u.uploaded_by_name}
                      </p>
                      {u.note ? <p className="mt-1 text-xs italic text-muted-foreground">«{u.note}»</p> : null}
                      {u.status === "avvist" && u.reject_reason ? <p className="mt-1 text-xs text-destructive">Avvist: {u.reject_reason}</p> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" data-testid={`upload-view-${u.id}`}
                        onClick={() => openSigned(`/share-uploads/${u.id}/file-link`).catch(() => toast.error("Kunne ikke åpne dokumentet"))}>
                        <FileText className="h-4 w-4" /> Se PDF
                      </Button>
                      {u.status === "til_godkjenning" ? (
                        <>
                          <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`upload-reject-${u.id}`} onClick={() => { setRejectUpload(u); setRejectReason(""); }}>
                            <X className="h-4 w-4" /> Avvis
                          </Button>
                          <Button size="sm" className="gap-1.5" data-testid={`upload-approve-${u.id}`} onClick={() => openApprove(u)}>
                            <Check className="h-4 w-4" /> Godkjenn
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-destructive/10 text-destructive"><Building2 className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Alle selskaper</h1>
            <p className="text-sm text-muted-foreground">Alle registrerte selskaper. Sletting fjerner selskapet og alle tilhørende data permanent.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-companies-list">
          {comps === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : comps.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen selskaper registrert ennå.
            </div>
          ) : (
            comps.map((c, i) => (
              <motion.div
                key={c.id}
                data-testid={`admin-company-${c.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-5 w-5" strokeWidth={1.7} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-headings text-base font-bold tracking-tight">{c.name}</p>
                    {dupMap[c.id] ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400" data-testid={`dup-badge-${c.id}`}>
                        <AlertTriangle className="h-3 w-3" /> Mulig duplikat
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.org_form || "AS"}{c.org_number ? ` · Org.nr ${c.org_number}` : " · uten org.nr"}{c.city ? ` · ${c.city}` : ""}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {c.member_count} medlem(mer) · {c.shareholder_count} aksjonær(er) · {c.meeting_count} møte(r)
                  </p>
                </div>
                {dupMap[c.id] ? (
                  <Button size="sm" variant="outline" className="gap-1.5" data-testid={`merge-company-${c.id}`} onClick={() => openMerge(dupMap[c.id])}>
                    <GitMerge className="h-4 w-4" /> Slå sammen
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`delete-company-${c.id}`} onClick={() => openDelete(c)}>
                  <Trash2 className="h-4 w-4" /> Slett
                </Button>
              </motion.div>
            ))
          )}
        </div>
        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><FileSignature className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Lisenser & fakturering</h1>
            <p className="text-sm text-muted-foreground">Murphy AS er avtalepart. Se alle lisenser, marker betalt/ikke betalt og følg MRR/ARR.</p>
          </div>
        </div>

        {/* MRR / ARR summary */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="license-summary">
          {[
            { key: "signed", label: "Signerte lisenser", value: `${lics?.summary?.signed ?? 0} / ${lics?.summary?.total ?? 0}`, icon: FileSignature },
            { key: "paid", label: "Betalte lisenser", value: `${lics?.summary?.paid ?? 0} / ${lics?.summary?.signed ?? 0}`, icon: CreditCard },
            { key: "mrr", label: "MRR (aktive signerte)", value: nok(lics?.summary?.mrr), icon: TrendingUp },
            { key: "arr", label: "ARR (aktive signerte)", value: nok(lics?.summary?.arr), icon: Coins },
          ].map((c) => (
            <div key={c.key} data-testid={`license-kpi-${c.key}`} className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
              <c.icon className="h-5 w-5 text-primary" strokeWidth={1.7} />
              <div className="mt-3 font-headings text-2xl font-extrabold tracking-tight">{lics ? c.value : <Skeleton className="h-7 w-16" />}</div>
              <p className="text-xs text-muted-foreground">{c.label}</p>
            </div>
          ))}
        </div>
        {lics?.summary?.mrr_exact != null ? (
          <p className="mt-2 text-[11px] text-muted-foreground">MRR beregnes som ARR ÷ 12 for alle signerte (aktive) lisenser. Eksakt MRR: {nok(lics.summary.mrr_exact)}.</p>
        ) : null}

        {/* Murphy AS / avtaleinnstillinger */}
        <div className="mt-8 rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="license-settings-card">
          <h2 className="font-headings text-lg font-bold tracking-tight">Murphy AS – avsender & avtale</h2>
          <p className="mt-1 text-sm text-muted-foreground">Opplysningene vises på lisensavtalen og fakturaen.</p>
          {lsettings === null ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Laster …</div>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="text-xs">Firmanavn</Label><Input className="mt-1" data-testid="settings-issuer-name" value={lsettings.issuer_name || ""} onChange={(e) => setField("issuer_name", e.target.value)} /></div>
                <div><Label className="text-xs">Organisasjonsnummer</Label><Input className="mt-1" data-testid="settings-issuer-orgnr" value={lsettings.issuer_org_number || ""} onChange={(e) => setField("issuer_org_number", e.target.value)} /></div>
                <div><Label className="text-xs">Adresse</Label><Input className="mt-1" data-testid="settings-issuer-address" value={lsettings.issuer_address || ""} onChange={(e) => setField("issuer_address", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Postnr</Label><Input className="mt-1" data-testid="settings-issuer-postal" value={lsettings.issuer_postal_code || ""} onChange={(e) => setField("issuer_postal_code", e.target.value)} /></div>
                  <div><Label className="text-xs">Poststed</Label><Input className="mt-1" data-testid="settings-issuer-city" value={lsettings.issuer_city || ""} onChange={(e) => setField("issuer_city", e.target.value)} /></div>
                </div>
                <div>
                  <Label className="text-xs">Faktura-e-post {!lsettings.issuer_email ? <span className="text-amber-600 dark:text-amber-400">· må fylles ut</span> : null}</Label>
                  <Input className="mt-1" type="email" placeholder="faktura@murphy.no" data-testid="settings-issuer-email" value={lsettings.issuer_email || ""} onChange={(e) => setField("issuer_email", e.target.value)} />
                </div>
                <div><Label className="text-xs">Årlig pris (kr)</Label><Input className="mt-1" type="number" inputMode="numeric" data-testid="settings-price" value={lsettings.price_nok ?? 1990} onChange={(e) => setField("price_nok", Number(e.target.value))} /></div>
              </div>
              <div>
                <Label className="text-xs">Avtalevilkår (markdown)</Label>
                <Textarea className="mt-1 min-h-[160px] font-mono text-xs" data-testid="settings-terms" value={lsettings.agreement_terms || ""} onChange={(e) => setField("agreement_terms", e.target.value)} />
              </div>
              <div className="flex justify-end">
                <Button className="gap-2" data-testid="settings-save-btn" onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre innstillinger
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Lisensliste */}
        <div className="mt-6 space-y-3" data-testid="admin-licenses-list">
          {lics === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          ) : (lics.licenses || []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <FileSignature className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen lisenser ennå.
            </div>
          ) : (
            lics.licenses.map((l, i) => (
              <motion.div
                key={l.id}
                data-testid={`admin-license-${l.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-5 w-5" strokeWidth={1.7} /></div>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-headings text-base font-bold tracking-tight">
                    {l.company_name}
                    {l.signed ? (
                      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Signert</span>
                    ) : (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">Ikke signert</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Faktura {l.invoice_number} · {nok(l.price_nok)}/år · Fornyes {fmtDate(l.renewal_date)}
                    {l.signed_by_name ? ` · Signert av ${l.signed_by_name}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={l.payment_status === "paid" ? "outline" : "default"}
                    className="gap-1.5"
                    data-testid={`license-payment-toggle-${l.id}`}
                    disabled={payingId === l.id}
                    onClick={() => togglePayment(l)}
                  >
                    {payingId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    {l.payment_status === "paid" ? "Betalt" : "Marker betalt"}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" data-testid={`license-invoice-${l.id}`}
                    onClick={() => openSigned(`/companies/${l.company_id}/license/invoice-link`).catch(() => toast.error("Kunne ikke åpne fakturaen"))}>
                    <Receipt className="h-4 w-4" /> Faktura
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Venter på signatur (på tvers av selskaper) */}
        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><FileSignature className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Venter på signatur</h1>
            <p className="text-sm text-muted-foreground">Alle sluttsedler og avtaler som venter på signatur på tvers av selskaper.</p>
          </div>
        </div>
        <div className="mt-6 space-y-3" data-testid="admin-pending-signatures-list">
          {pendingSigs === null ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
          ) : pendingSigs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              <FileSignature className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              Ingen dokumenter venter på signatur akkurat nå.
            </div>
          ) : (
            pendingSigs.map((s, i) => (
              <motion.div key={`${s.entity_type}-${s.entity_id}`} data-testid={`admin-pending-sig-${s.entity_id}`}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <button type="button" data-testid={`pending-open-${s.entity_id}`} onClick={() => openPendingPdf(s)}
                    className="min-w-0 flex-1 rounded-lg text-left transition-colors hover:text-primary">
                    <p className="flex items-center gap-1.5 font-headings text-base font-bold tracking-tight">
                      <FileText className="h-4 w-4 shrink-0 text-primary" /> {s.title} · {s.company_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.entity_type === "settlement" ? "Sluttseddel" : "Avtale"}
                      {s.shares ? ` · ${Number(s.shares).toLocaleString("nb-NO")} ${s.share_class}` : ""}
                      {s.price_per_share ? ` · ${nok(s.price_per_share)}/aksje` : ""} · Opprettet {fmtDate(s.created_at)} · Klikk for å åpne PDF
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">{s.signed_count}/{s.party_count} signert</span>
                    <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" data-testid={`pending-open-pdf-${s.entity_id}`} onClick={() => openPendingPdf(s)}>
                      <FileText className="h-3.5 w-3.5" /> Se PDF
                    </Button>
                  </div>
                </div>
                <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
                  {s.parties.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-xs">
                      {p.signed ? <Check className="h-4 w-4 shrink-0 text-emerald-600" /> : <Clock className="h-4 w-4 shrink-0 text-amber-500" />}
                      <span className="font-medium">{p.role}:</span>
                      <span className="truncate">{p.name}</span>
                      {!p.signed && p.waiting ? <span className="ml-auto truncate text-muted-foreground">→ {p.waiting}</span> : null}
                      {!p.signed && p.can_remind ? (
                        <Button size="sm" variant="outline" className="ml-auto h-6 shrink-0 gap-1 px-2 text-[11px]"
                          data-testid={`pending-remind-${p.signing_request_id}`} disabled={remindingId === p.signing_request_id}
                          onClick={() => remindParty(p)}>
                          {remindingId === p.signing_request_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Purr
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Brukere */}
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Users className="h-5 w-5" /></div>
          <div className="mr-auto">
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Brukere</h1>
            <p className="text-sm text-muted-foreground">Rediger, gi admin-tilgang, send passord-lenke eller slett kontoer.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Søk på navn eller e-post …" className="pl-9" />
          </div>
        </div>
        <div className="mt-6 space-y-2" data-testid="admin-users-list">
          {users === null ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)
          ) : users.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">Ingen brukere.</div>
          ) : filteredUsers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="admin-users-no-match">Ingen brukere treffer «{userSearch}».</div>
          ) : (
            filteredUsers.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-3.5 shadow-sm">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {u.name || "(uten navn)"}
                    {u.is_admin ? <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"><ShieldCheck className="h-3 w-3" />Admin</span> : null}
                    {u.identity_verified ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300"><ShieldCheck className="h-3 w-3" />Verifisert</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {u.email || "ingen e-post"} · {u.company_count} selskap{u.company_count === 1 ? "" : "er"}
                    {u.created_at ? ` · registrert ${fmtDate(u.created_at)}` : ""}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground" title={u.id === user?.id ? "Du kan ikke fjerne din egen admin-tilgang" : "Gi eller fjern admin-tilgang"}>
                  <ShieldCheck className="h-3.5 w-3.5" /> Admin
                  <Switch
                    data-testid={`admin-user-role-${u.id}`}
                    checked={!!u.is_admin}
                    disabled={togglingId === u.id || u.id === user?.id}
                    onCheckedChange={(v) => toggleAdmin(u, v)}
                  />
                </label>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid={`admin-user-reset-${u.id}`} disabled={!u.email || resettingId === u.id} onClick={() => sendReset(u)}>
                  {resettingId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Send passord-lenke
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid={`admin-user-edit-${u.id}`} onClick={() => openEditUser(u)}>
                  <PenLine className="h-4 w-4" /> Endre
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive"
                  data-testid={`admin-user-delete-${u.id}`} disabled={u.is_admin || u.id === user?.id} onClick={() => setDelUser(u)}>
                  <Trash2 className="h-4 w-4" /> Slett
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Aktivitetslogg */}
        <div className="mt-12 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><History className="h-5 w-5" /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight">Aktivitetslogg</h1>
            <p className="text-sm text-muted-foreground">Siste administrasjonshandlinger på brukere.</p>
          </div>
        </div>
        <div className="mt-6 space-y-2" data-testid="admin-audit-list">
          {audit === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)
          ) : audit.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="admin-audit-empty">Ingen handlinger logget ennå.</div>
          ) : (
            audit.map((a) => (
              <div key={a.id} data-testid={`admin-audit-${a.id}`} className="flex items-start gap-3 rounded-xl border border-border/60 bg-card p-3 text-sm shadow-sm">
                <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-muted">{auditIcon(a.action)}</div>
                <div className="min-w-0 flex-1">
                  <p><span className="font-medium">{a.actor_name || "Admin"}</span> {auditLabel(a.action)} <span className="font-medium">{a.target_name || a.target_email || "en bruker"}</span></p>
                  {a.details ? <p className="mt-0.5 text-xs text-muted-foreground">{a.details}</p> : null}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Slå sammen duplikat-selskaper */}
      <Dialog open={!!mergeGroup} onOpenChange={(o) => { if (!o) { setMergeGroup(null); setMergeTarget(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="merge-companies-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-primary" /> Slå sammen duplikater</DialogTitle>
            <DialogDescription>
              Velg selskapet som skal <strong>beholdes</strong>. Medlemmer og alle data fra de andre flyttes hit, og duplikatene slettes permanent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(mergeGroup || []).map((c) => {
              const active = mergeTarget === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`merge-target-${c.id}`}
                  onClick={() => setMergeTarget(c.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                >
                  <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border ${active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                    {active ? <Check className="h-4 w-4" /> : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{c.name} {active ? <span className="text-primary">(beholdes)</span> : null}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {c.org_number ? `Org.nr ${c.org_number}` : "uten org.nr"} · {c.member_count} medlem(mer) · {c.shareholder_count} aksjonær(er) · {c.meeting_count} møte(r)
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            Dette kan ikke angres. {(mergeGroup || []).length - 1} selskap vil bli slått sammen og slettet.
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setMergeGroup(null); setMergeTarget(null); }} disabled={merging} data-testid="merge-cancel">Avbryt</Button>
            <Button className="gap-2" onClick={doMerge} disabled={merging || !mergeTarget} data-testid="merge-confirm">
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />} Slå sammen &amp; slett duplikat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delCompany} onOpenChange={(o) => { if (!o) setDelCompany(null); }}>
        <AlertDialogContent data-testid="delete-company-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette «{delCompany?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Dette sletter selskapet og <span className="font-semibold text-destructive">alle tilhørende data permanent</span> – medlemmer, aksjonærer, møter, protokoller, dokumenter, utbytter og transaksjoner. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Skriv selskapsnavnet for å bekrefte:</p>
            <Input
              data-testid="delete-company-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={delCompany?.name || ""}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-company-cancel" disabled={deleting}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-company-confirm"
              disabled={deleting || confirmText.trim() !== (delCompany?.name || "").trim()}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Slett permanent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!approveUpload} onOpenChange={(o) => { if (!o) setApproveUpload(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="approve-upload-dialog">
          <DialogHeader>
            <DialogTitle>Registrer transaksjon</DialogTitle>
            <DialogDescription>Bekreft detaljene fra den opplastede avtalen. Aksjeboka oppdateres og PDF-en lagres som dokumentasjon.</DialogDescription>
          </DialogHeader>
          {af ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Dato</Label><Input type="date" className="mt-1" data-testid="approve-date" value={af.date} onChange={(e) => setAff("date", e.target.value)} /></div>
                <div><Label className="text-xs">Aksjeklasse</Label><Input className="mt-1" data-testid="approve-class" value={af.share_class} onChange={(e) => setAff("share_class", e.target.value)} /></div>
              </div>
              <div><Label className="text-xs">Tegningskurs / kurs per aksje (kr)</Label><Input type="number" className="mt-1" data-testid="approve-price" value={af.price_per_share} onChange={(e) => setAff("price_per_share", e.target.value)} /></div>
              {(approveUpload?.subscribers || []).length ? (
                <div className="rounded-lg border border-border/60 bg-muted/40 p-3" data-testid="approve-subscribers">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Investorer i emisjonen ({approveUpload.subscribers.length}) · totalt {approveUpload.subscribers.reduce((s, x) => s + (x.shares || 0), 0).toLocaleString("nb-NO")} aksjer</p>
                  <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
                    {approveUpload.subscribers.map((s, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md bg-background px-2.5 py-1.5 text-sm">
                        <span className="truncate">{s.name}{s.org ? <span className="ml-1 text-xs text-muted-foreground">· {s.org}</span> : null}</span>
                        <span className="shrink-0 font-medium">{(s.shares || 0).toLocaleString("nb-NO")}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">Hver investor føres inn som egen emisjonstransaksjon (nyutstedelse).</p>
                </div>
              ) : (
                <>
                  <div><Label className="text-xs">Antall aksjer</Label><Input type="number" className="mt-1" data-testid="approve-shares" value={af.shares} onChange={(e) => setAff("shares", e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Selger (fra)</Label><Input className="mt-1" data-testid="approve-from" value={af.from_name} onChange={(e) => setAff("from_name", e.target.value)} placeholder="Tom = nyutstedelse" /></div>
                    <div><Label className="text-xs">Kjøper (til)</Label><Input className="mt-1" data-testid="approve-to" value={af.to_name} onChange={(e) => setAff("to_name", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Selger org.nr (valgfritt)</Label><Input className="mt-1" data-testid="approve-from-org" value={af.from_org} onChange={(e) => setAff("from_org", e.target.value)} placeholder="9 siffer for selskap" /></div>
                    <div><Label className="text-xs">Kjøper org.nr (valgfritt)</Label><Input className="mt-1" data-testid="approve-to-org" value={af.to_org} onChange={(e) => setAff("to_org", e.target.value)} placeholder="9 siffer for selskap" /></div>
                  </div>
                </>
              )}
              <div><Label className="text-xs">Transaksjonstype</Label><Input className="mt-1" data-testid="approve-type" value={af.type} onChange={(e) => setAff("type", e.target.value)} /></div>
              <div><Label className="text-xs">Merknad</Label><Textarea className="mt-1" rows={2} value={af.note} onChange={(e) => setAff("note", e.target.value)} /></div>
              <p className="text-[11px] text-muted-foreground">Selger må matche et eksisterende navn i aksjeboka for at aksjene skal trekkes fra. Org.nr fylles automatisk fra Proff/AI hvis oppgitt – nye selskapsaksjonærer får da riktig org.nr. La stå tom ved nyutstedelse.</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveUpload(null)}>Avbryt</Button>
            <Button onClick={confirmApprove} disabled={approving} className="gap-2" data-testid="approve-upload-confirm">
              {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Godkjenn og registrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!rejectUpload} onOpenChange={(o) => { if (!o) setRejectUpload(null); }}>
        <AlertDialogContent data-testid="reject-upload-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Avvis opplasting?</AlertDialogTitle>
            <AlertDialogDescription>Skriv en kort begrunnelse (valgfritt). Brukeren ser statusen på opplastingen.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Begrunnelse …" rows={3} />
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction data-testid="reject-upload-confirm" onClick={(e) => { e.preventDefault(); confirmReject(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Avvis
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delUser} onOpenChange={(o) => { if (!o) setDelUser(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette «{delUser?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Dette sletter brukerkontoen ({delUser?.email || "ingen e-post"}) og fjerner personen fra {delUser?.company_count} selskap{delUser?.company_count === 1 ? "" : "er"} (styreverv og tilganger). Aksjonærposter i aksjeeierbøker påvirkes ikke. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>Avbryt</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-user-confirm" disabled={deletingUser}
              onClick={(e) => { e.preventDefault(); confirmDeleteUser(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deletingUser ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Slett bruker
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Endre bruker</DialogTitle>
            <DialogDescription>Oppdater navn og e-postadresse. E-post brukes til innlogging.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-user-name">Navn</Label>
              <Input id="edit-user-name" data-testid="edit-user-name-input" className="mt-1.5" value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="edit-user-email">E-post</Label>
              <Input id="edit-user-email" data-testid="edit-user-email-input" type="email" className="mt-1.5" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)} disabled={savingUser}>Avbryt</Button>
            <Button onClick={saveUser} disabled={savingUser} data-testid="edit-user-save">
              {savingUser ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Lagre endringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
